'use client';
/**
 * nav_region1 — 홈(/) 지역 필터 섬. 「이번 주 청약」「분양예정」「재개발·재건축」「분석 글」 4섹션을 고른 지역으로 갈아 그리고,
 * 「지역별 분양」 은 그 시·도 타일을 강조한다. 맨 위에 「{지역} 전체 보기 → 허브」 띠.
 *
 *   <HomeRegionBar />                                  홈 히어로 바로 아래 1개(띠 · 가상 페이지뷰 · 타일 강조)
 *   <HomeRegionSlot kind="schedule">{서버 목록}</HomeRegionSlot>   섹션마다(지역이 없으면 서버 목록 그대로)
 *
 * 규칙:
 *   · 서버 HTML·첫 수화 렌더 = 전국(children 그대로). 지역은 useEffect 뒤에만 정한다(봇·일반 동일 HTML · 클로킹 0).
 *   · 지역 = ?r=(URL) > 기억(kd_nav_region_v1, 마운트 1회 — «재방문 기본값») > 전국. resolveHomeRegion(순수).
 *   · 재료 = GET /api/home/region?code=(CDN 600초 · Redis 0). 실패·무효 코드 → 전국 그대로.
 *   · 0건 섹션 = 빈 상자 대신 «이 지역 없음 + 인접 지역 / 전국 보기».
 */
import React from 'react'; // vitest(클래식 JSX) 렌더 검증용
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { useRegionParam, emitRegionParam } from '@/lib/nav/use-region-param';
import { readRegionMemory, writeRegionMemory } from '@/lib/nav/region-scope';
import { trackRx } from '@/lib/rx/track';
import { SITE_URL } from '@/lib/constants';
import { sortRootAttrs, sortKeyAttrs } from '@/lib/ds2/sort';
import Thumb from '@/components/thumb/Thumb';
import { SchedGrid, DateCard, SiteCard, SiteCardGrid, RedevList, PostGrid, PostCard } from '@/components/ds2';
import {
  resolveHomeRegion, homeVirtualPath, upcomingMoreLabel, homeRegionEmptyLine, homeRegionFallbackLine,
  type HomeRegionKind, type HomeRegionPayload, type HomeRegionSource,
} from '@/lib/home/region-home-pure';
import './home-region.css';

export const HOME_REGION_MEMORY_EVENT = 'kd:region-memory'; // GlobalRegionChip REGION_MEMORY_EVENT 와 같은 이름(순환 import 피함)

type HomeRegionState =
  /** settled = 수화 뒤 판정까지 끝난 전국(첫 화면 가림 해제 신호 · region_home1). 첫 렌더(수화 전)는 settled 없음. */
  | { status: 'national'; settled?: boolean }
  | { status: 'loading'; code: string; source: HomeRegionSource }
  | { status: 'ready'; code: string; source: HomeRegionSource; data: HomeRegionPayload };

const cache = new Map<string, Promise<HomeRegionPayload | null>>();

export function loadHomeRegionClient(code: string): Promise<HomeRegionPayload | null> {
  const hit = cache.get(code);
  if (hit) return hit;
  const p = fetch(`/api/home/region?code=${encodeURIComponent(code)}`)
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => (j && j.ok === true ? (j as HomeRegionPayload) : null))
    .catch(() => null);
  p.then((v) => { if (!v) cache.delete(code); });
  cache.set(code, p);
  return p;
}

/** 섬 공용 훅 — 각 섬이 따로 불러도 fetch 는 코드당 1회(cache). */
export function useHomeRegion(): HomeRegionState {
  const param = useRegionParam();
  const [state, setState] = useState<HomeRegionState>({ status: 'national' });
  const first = useRef(true);
  const srcRef = useRef<HomeRegionSource | null>(null);

  useEffect(() => {
    if (param === undefined) return; // 수화 전 — 서버와 같은 전국
    const phase = first.current ? 'mount' : 'change';
    first.current = false;
    const pick = resolveHomeRegion(param, readRegionMemory(), phase);
    if (!pick) { srcRef.current = null; setState({ status: 'national', settled: true }); return; }
    let alive = true;
    srcRef.current = pick.source;
    setState({ status: 'loading', code: pick.code, source: pick.source });
    loadHomeRegionClient(pick.code).then((data) => {
      if (!alive) return;
      setState(data ? { status: 'ready', code: pick.code, source: pick.source, data } : { status: 'national', settled: true });
    });
    return () => { alive = false; };
  }, [param]);

  // 기억이 지워지면(칩·띠 「전국」) 기억으로 적용 중이던 필터를 푼다. ?r= 로 적용 중이면 URL 이 이긴다.
  useEffect(() => {
    const onMem = () => { if (srcRef.current === 'memory' && !readRegionMemory()) { srcRef.current = null; setState({ status: 'national', settled: true }); } };
    window.addEventListener(HOME_REGION_MEMORY_EVENT, onMem);
    return () => window.removeEventListener(HOME_REGION_MEMORY_EVENT, onMem);
  }, []);

  return state;
}

/** 홈에서 지역 바꾸기(인접 지역 버튼) — 칩과 같은 적용(?r= · 기억 · 알림 · 계측). */
export function useApplyHomeRegion() {
  const router = useRouter();
  return (next: { code: string; sido: string | null } | null, variant: string) => {
    const target = next ? homeVirtualPath(next.code) : '/';
    const cur = window.location.pathname + window.location.search;
    trackRx('region_select_apply', next?.code ?? 'national', `${variant}:${target === cur ? 'noop' : 'filtered'}`, { target });
    writeRegionMemory(next?.code ?? null, next?.sido ? { sido: next.sido } : null);
    try { window.dispatchEvent(new Event(HOME_REGION_MEMORY_EVENT)); } catch { /* 무시 */ }
    if (target !== cur) router.push(target, { scroll: false });
    emitRegionParam(next?.code ?? null);
  };
}

/** region_home1 규칙 3 — 0건 섹션 = 한 줄 「부산은 이번 주 청약 없음 · 전국 보기」(+ 인접 지역) · 빈 카드 0. */
function Empty({ kind, data }: { kind: HomeRegionKind; data: HomeRegionPayload }) {
  const apply = useApplyHomeRegion();
  return (
    <div className="clh-rf-empty" data-home-region-empty={kind}>
      <p>{homeRegionEmptyLine(data.label, kind)}</p>
      <div className="clh-rf-acts">
        {data.neighbors.map((n) => (
          <button key={n.code} type="button" className="clh-rf-btn" onClick={() => apply({ code: n.code, sido: n.label }, 'home-empty')}>
            {n.label} 보기
          </button>
        ))}
        <button type="button" className="clh-rf-btn" data-home-region-national="" onClick={() => apply(null, 'home-empty')}>전국 보기</button>
      </div>
    </div>
  );
}

export type HomeUpcomingItem = HomeRegionPayload['upcoming'][number];

/** 순수 — Fisher–Yates(새 배열). rnd 주입 = 테스트. */
export function shuffleItems<T>(xs: readonly T[], rnd: () => number = Math.random): T[] {
  const a = xs.slice();
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
  return a;
}

/**
 * home-shuffle(2026-10-26 · Node 「분양예정 순서 랜덤으로」) — 분양예정 카드 줄. 서버 HTML·첫 수화 = 서버 순서(봇·사람 같은 HTML · 클로킹 0),
 *   마운트 뒤(useEffect) 볼 때마다 순서를 섞는다(React 상태 — DOM 직접 조작 0 · 캐러셀 화살표는 MutationObserver 로 다시 잰다).
 */
export function HomeUpcomingGrid({ ariaLabel, items }: { ariaLabel: string; items: readonly HomeUpcomingItem[] }) {
  const [list, setList] = useState<readonly HomeUpcomingItem[]>(items);
  useEffect(() => { setList(shuffleItems(items)); }, [items]);
  return (
    <SiteCardGrid ariaLabel={ariaLabel} cols={5} dataAttrs={{ 'data-home-shuffle': '' }} slides>
      {list.map((c) => (
        <SiteCard
          key={c.key} title={c.title} href={c.href} thumbSrc={c.thumbSrc} thumbAlt={c.thumbAlt} status="분양예정"
          periodLabel={c.periodLabel} periodTone={c.periodTone} meta={c.meta} sub={c.sub} dataAttrs={sortKeyAttrs(c.sortKey)}
        />
      ))}
    </SiteCardGrid>
  );
}

const AI_NOTICE = '이미지는 공개 자료를 바탕으로 만든 AI 상상도이며 실제와 다를 수 있습니다.';

/** 레인 E — 시·군 0건 섹션을 상위 시·도 목록으로 채웠을 때 목록 위 한 줄(+ 그 시·도로 바꾸기). */
function FallbackNote({ kind, data }: { kind: HomeRegionKind; data: HomeRegionPayload }) {
  const apply = useApplyHomeRegion();
  const fb = data.fallback?.[kind];
  if (!fb) return null;
  return (
    <div className="clh-rf-empty" data-home-region-fallback={kind}>
      <p>{homeRegionFallbackLine(data.label, kind, fb.label)}</p>
      <div className="clh-rf-acts">
        <button type="button" className="clh-rf-btn" onClick={() => apply({ code: fb.code, sido: fb.label }, 'home-fallback')}>{fb.label} 보기</button>
      </div>
    </div>
  );
}

function RegionList({ kind, data }: { kind: HomeRegionKind; data: HomeRegionPayload }) {
  return (
    <>
      <FallbackNote kind={kind} data={data} />
      <RegionListBody kind={kind} data={data} />
    </>
  );
}

function RegionListBody({ kind, data }: { kind: HomeRegionKind; data: HomeRegionPayload }) {
  const lab = data.fallback?.[kind]?.label ?? data.label; // 레인 E — 상위 지역으로 채운 목록은 그 지역 이름으로 읽힌다
  if (kind === 'schedule') {
    if (!data.schedule.length) return <Empty kind={kind} data={data} />;
    return (
      <SchedGrid ariaLabel={`${lab} 이번 주 청약 일정`} dataAttrs={sortRootAttrs('schedule')} slides>
        {data.schedule.map((c) => (
          <DateCard key={c.key} title={c.title} href={c.href} date={c.date} meta={c.meta} days={c.days} dataAttrs={sortKeyAttrs(c.sortKey)} thumb={c.thumb ? <Thumb data={c.thumb} w={48} h={48} /> : null} />
        ))}
      </SchedGrid>
    );
  }
  if (kind === 'upcoming') {
    if (!data.upcoming.length) return <Empty kind={kind} data={data} />;
    return (
      <>
        <HomeUpcomingGrid ariaLabel={`${lab} 분양예정 현장`} items={data.upcoming} />
        {data.upcoming.some((c) => c.ai) ? <p className="ds2-ainote" data-cl-ai-notice="">{AI_NOTICE}</p> : null}
      </>
    );
  }
  if (kind === 'redev') {
    if (!data.redev.length) return <Empty kind={kind} data={data} />;
    return (
      <RedevList
        cols={3}
        slides
        dataAttrs={sortRootAttrs('redev')}
        rows={data.redev.map((r) => ({ name: r.name, href: r.href, meta: r.meta, step: r.step, total: r.total, label: r.label, dataAttrs: sortKeyAttrs(r.sortKey), thumb: r.thumb ? <Thumb data={r.thumb} w={48} h={48} /> : null }))}
      />
    );
  }
  if (!data.analysis.length) return <Empty kind={kind} data={data} />;
  return (
    <PostGrid ariaLabel={`${lab} 최근 분석 글`} dataAttrs={sortRootAttrs('posts')}>
      {data.analysis.map((b) => (
        <PostCard key={b.key} title={b.title} href={b.href} thumbSrc={b.thumbSrc} thumbAlt={b.thumbAlt} meta={b.meta} dataAttrs={sortKeyAttrs(b.sortKey)} />
      ))}
    </PostGrid>
  );
}

/** 섹션 본문 자리 — 지역이 정해지면 그 지역 목록(0건이면 안내), 아니면 서버 목록 그대로. */
export function HomeRegionSlot({ kind, children }: { kind: HomeRegionKind; children?: ReactNode }) {
  const st = useHomeRegion();
  if (st.status !== 'ready') {
    return <div className="clh-rf-slot" data-home-region-slot={kind} aria-busy={st.status === 'loading' ? true : undefined}>{children}</div>;
  }
  return (
    <div className="clh-rf-slot" data-home-region-slot={kind} data-home-region={st.data.code}>
      <RegionList kind={kind} data={st.data} />
    </div>
  );
}

/**
 * post1 3 — 「분양예정」 머리 「전체 N곳 →」. 서버·첫 수화 = 전국(href·문구 그대로 · 종전과 같은 <a>).
 *   지역이 정해지면 그 지역 수(upcomingCount · 전국과 같은 정의)와 그 지역 목록(upcomingHref · 없으면 지역 허브)으로 바꾼다.
 */
export function HomeUpcomingMore({ href, label }: { href: string; label: string }) {
  const st = useHomeRegion();
  if (st.status !== 'ready') return <a href={href}>{label}</a>;
  const d = st.data;
  return <a href={d.upcomingHref || d.hubHref} data-home-region-more={d.code}>{upcomingMoreLabel(d.upcomingCount ?? null)}</a>;
}

/** 「지역별 분양」 타일 + 히어로 시도 칩 강조(서버 href = regionHubHref(top) 와 같은 값 · region_home1: 칩도 지역 범위). */
const HL_ON = '[data-cl-section="sido"] a[data-home-region-on], [data-cl-section="search"] .ds2-chips a[data-home-region-on]';
const HL_ALL = '[data-cl-section="sido"] a[href], [data-cl-section="search"] .ds2-chips a[href]';
function highlightSido(href: string | null): void {
  document.querySelectorAll<HTMLAnchorElement>(HL_ON).forEach((a) => {
    a.removeAttribute('data-home-region-on');
    a.removeAttribute('aria-current');
  });
  if (!href) return;
  document.querySelectorAll<HTMLAnchorElement>(HL_ALL).forEach((a) => {
    if (a.getAttribute('href') === href) { a.setAttribute('data-home-region-on', ''); a.setAttribute('aria-current', 'true'); }
  });
}

/** 가상 페이지뷰 — 같은 경로(/)에서 ?r= 만 바뀌면 PageViewTracker(pathname effect)가 안 돈다. conversion_events + GA4(있으면). */
export function emitHomeVirtualPageview(path: string, source: HomeRegionSource | 'national'): void {
  trackRx('virtual_pageview', path, source);
  try {
    const w = window as unknown as { gtag?: (...a: unknown[]) => void };
    if (typeof w.gtag === 'function') w.gtag('event', 'page_view', { page_path: path, page_location: `${SITE_URL}${path}`, kd_virtual: 1 });
  } catch { /* 무시 */ }
}

/** 첫 화면 가림 해제(html[data-kd-hr] 제거). 판정 전(수화 전 전국)에는 부르지 않는다. */
export function releaseHomeRegionPrepaint(): void {
  try { document.documentElement.removeAttribute('data-kd-hr'); } catch { /* 무시 */ }
}

export function HomeRegionBar() {
  const st = useHomeRegion();
  const apply = useApplyHomeRegion();
  const last = useRef<string | null>(null);

  useEffect(() => {
    if (st.status === 'loading') return;
    // region_home1 규칙 4 — 첫 화면 가림(HomeRegionPrepaint 의 html[data-kd-hr=pending]) 해제: 지역이 그려졌거나 전국으로 판정이 끝났을 때만.
    if (st.status === 'ready' || st.settled) releaseHomeRegionPrepaint();
    const key = st.status === 'ready' ? `${st.code}|${st.source}` : 'national';
    highlightSido(st.status === 'ready' ? st.data.sidoHref : null);
    if (last.current === null && key === 'national') { last.current = key; return; } // 첫 전국 = 실제 PV 가 이미 찍혔다
    if (last.current === key) return;
    last.current = key;
    if (st.status === 'ready') emitHomeVirtualPageview(homeVirtualPath(st.code), st.source);
    else emitHomeVirtualPageview('/', 'national');
  }, [st]);

  // home-tabs(2026-10-28) — 「{지역} 기준으로 보는 중 · 전체 보기 · 전국 보기」 띠는 지역 탭(HomeRegionTabs)이 대신 보인다.
  //   이 섬은 효과(가상 페이지뷰 · 타일 강조 · 첫 화면 가림 해제)만 맡는다.
  void apply;
  return null;
}

